import { Link, useMatchRoute, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Fragment } from 'react';

import { useSidebarIsFloating } from '#state/atoms/sidebar-layout.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { ProjectCrumb } from './project-crumb.js';
import { RevisionCrumb } from './revision-crumb.js';

import type { ReactNode } from 'react';

/**
 * Id of the breadcrumb row's far-right slot. A route scoped to what the slot should show portals
 * its own content in, so the shell stays ignorant of what any given route puts there.
 */
export const BREADCRUMB_ACTIONS_ELEMENT_ID = 'breadcrumbs-actions';

export function AppBreadcrumbs() {
	const params = useParams({ strict: false });
	const matchRoute = useMatchRoute();
	const sidebarIsFloating = useSidebarIsFloating();
	const orgName = 'orgName' in params ? params.orgName : undefined;
	const projectId = 'projectId' in params ? params.projectId : undefined;
	const revisionId = 'revisionId' in params ? params.revisionId : undefined;
	const isInOrgSettings = matchRoute({ to: '/orgs/$orgName/settings', fuzzy: true }) !== false;
	const isCreatingProject = matchRoute({ to: '/orgs/$orgName/projects/new/upload' }) !== false;

	let slugs: Array<{ key: string; content: ReactNode }> = [];

	if (orgName) {
		slugs.push({
			key: 'orgName',
			content: (
				<Link
					to='/orgs/$orgName/projects'
					params={{ orgName }}
					className='text-gray-a9 transition-colors hover:text-gray-a11'
				>
					{orgName}
				</Link>
			),
		});

		if (isInOrgSettings) {
			slugs.push({
				key: 'settings',
				content: <span>Settings</span>,
			});
		} else if (isCreatingProject) {
			slugs.push({
				key: 'newProject',
				content: <span>New project</span>,
			});
		} else if (projectId) {
			slugs.push({
				key: 'projectName',
				content: <ProjectCrumb orgName={orgName} projectId={projectId} revisionId={revisionId} />,
			});

			if (revisionId) {
				slugs.push({
					key: 'revision',
					content: <RevisionCrumb projectId={projectId} revisionId={revisionId} />,
				});
			}
		}
	}

	// If the only slug is the org name, don't bother rendering anything at all
	if (slugs.length === 1) {
		slugs = [];
	}

	const el = useDataElTagGenerator(AppBreadcrumbs);
	return (
		<div
			{...el()}
			className={clsx(
				'flex h-8 shrink-0 items-center justify-between gap-4 px-3 text-sm',
				'not-has-[>:not(:empty)]:hidden', // If all direct children are empty, hide the breadcrumbs element completely so as to not occupy space in the layout
				sidebarIsFloating && 'pl-12',
			)}
		>
			<div {...el('breadcrumbs')} className='flex items-center gap-2 whitespace-nowrap text-gray-a11'>
				{/* Render each slug separated by a separator */}
				{slugs.flatMap(({ key, content }, i) =>
					i === 0 ?
						[<Fragment key={key}>{content}</Fragment>]
					:	[
							<span key={`separator-${key}`} {...el('separator')} className='text-gray-a7'>
								/
							</span>,
							<Fragment key={key}>{content}</Fragment>,
						],
				)}
			</div>

			<div {...el('actionsSlot')} id={BREADCRUMB_ACTIONS_ELEMENT_ID} />
		</div>
	);
}
